import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { rhybusWhoHttp } from '../api/rhybus-who-http'
import { ListNames } from '../config/configuration'
import type { I_NotificationMetadata } from '../interfaces'

type NotificationType = 'Action' | 'Notification'

type SendNotificationParams = {
	title: string
	activity: string
	subject: string
	notificationType: NotificationType
	status: string
	itemGuid?: string
	appId?: string
	toUser?: string
	toUserRole?: string
}

export const ACTIVITY_ID = {
	CaseSubmit: 'CASE_SUBMITTED',
	CaseUpdate: 'CaseUpdate',
	TrainingSubmit: 'TrainingSubmit',
	TrainingUpdate: 'TrainingUpdate',
	TrainingSeatValidation: 'TrainingSeatValidation',
	CASE_Approve: 'CASE_Approve',
	CaseDiscussion: 'CASE_Discussion',
	TrainingDiscussion: 'Training_Discussion',
} as const

export type ActivityIDValue = typeof ACTIVITY_ID[keyof typeof ACTIVITY_ID]

// ============================================================
// Basic single notification (no targeting — existing usage)
// ============================================================
export const sendNotification = async ({
	title,
	activity,
	subject,
	notificationType,
	status,
	itemGuid = '',
	appId,
	toUser = '',
	toUserRole = '',
}: SendNotificationParams): Promise<void> => {
	const now = new Date()
	const resolvedItemGuid = itemGuid || ''

	await (await rhybusRListsHttp().getList(ListNames.NOTIFICATION)).addOrUpdateItem({
		// Title: title,
		// Activity: activity,
		Subject: subject,
		NotificationType: notificationType,
		StatusCode: status !== '' && status != null ? parseInt(String(status), 10) || undefined : undefined,
		ItemGUID: resolvedItemGuid,
		InquiryGUID: resolvedItemGuid,
		// AppID: appId ?? resolvedItemGuid,
		ToUser: toUser,
		ToUserRole: toUserRole,
		// Role: '',
		IsRead: false,
		ItemCreated: now,
		ItemModified: now,
		// ItemCreatedBy: me?.id ?? 0,
		// ItemModifiedById: 0,
	} as any)

	const [{ default: store }, { getAllNotifications }] = await Promise.all([
		import('../store'),
		import('./notificationsSlice'),
	])
	store.dispatch(getAllNotifications())
}

// ============================================================
// Fetch all notification metadata rows from the list
// ============================================================
export const fetchNotificationMetadata = async (): Promise<I_NotificationMetadata[]> => {
	try {
		const items = [...(await (await rhybusRListsHttp().getList(ListNames.NOTIFICATION_METADATA)).query().get<any>())]
		return items.map((item: any) => ({
			eKey: item?.eKey ?? '',
			eTag: item?.eTag ?? '',
			Title: item?.Title ?? '',
			ActivityID: item?.ActivityID ?? item?.Activity_ID ?? '',
			AlertType: item?.AlertType ?? '',
			StatusCode: item?.StatusCode ?? '',
			SecurityGroupTitle: item?.SecurityGroupTitle ?? '',
			ToRole: item?.ToRole ?? '',
			Subject: item?.Subject ?? '',
			IsActive: item?.IsActive ?? true,
			ToUserRoles: item?.ToUserRoles ?? '',
		}))
	} catch (error) {
		console.error('[fetchNotificationMetadata]', error)
		return []
	}
}

// ============================================================
// Push targeted bell/action notifications to the Notifications list
// One notification record per user in each matched group
// ============================================================
const pushNotificationsToDashboard = async (
	itemGuid: string,
	subject: string,
	activityID: string,
	notifications: I_NotificationMetadata[],
): Promise<void> => {
	if (!notifications.length) return

	const now = new Date()
	const notifList = await rhybusRListsHttp().getList(ListNames.NOTIFICATION)
	const who = rhybusWhoHttp()

	for (const meta of notifications) {
		const groupName = meta.SecurityGroupTitle ?? ''
		if (!groupName) continue

		let groupUsers: any[] = []
		try {
			groupUsers = [...(await who.Role(groupName))] as any[]
		} catch (err) {
			console.error(`[pushNotificationsToDashboard] Failed to fetch group "${groupName}":`, err)
			continue
		}

		await Promise.all(
			groupUsers.map(async (user: any) => {
				const toUser: string =
					user?.Email ?? user?.email ?? user?.upn ?? user?.loginName ?? user?.LoginName ?? ''
				if (!toUser) return

				try {
					await notifList.addOrUpdateItem({
						Title: meta.Title ?? activityID,
						Activity: activityID,
						Subject: subject || meta.Subject || activityID,
						NotificationType: (meta.AlertType === 'ACTION' ? 'Action' : 'Notification') as 'Action' | 'Notification',
						Status: 'Active',
						ItemGUID: itemGuid,
						AppID: itemGuid,
						ToUser: toUser,
						ToUserRole: meta.ToRole ?? groupName,
						Role: groupName,
						IsRead: false,
						ItemCreated: now,
						ItemModified: now,
						ItemCreatedById: 0,
						ItemModifiedById: 0,
					} as any)
				} catch (err) {
					console.error(`[pushNotificationsToDashboard] Failed to save notification for "${toUser}":`, err)
				}
			})
		)
	}
}

// ============================================================
// Main trigger — mirrors triggerTRMNotifications from reference
// Call this after any case/training action
// ============================================================
export const triggerCaseNotifications = async (
	itemGuid: string,
	subject: string,
	activityID: ActivityIDValue | string,
	statusCode?: string | number,
	submitterEmail?: string,
): Promise<void> => {
	try {
		const allMetadata = await fetchNotificationMetadata()

		// Filter to active metadata rows matching this activityID
		const matchingMetadata = allMetadata.filter(
			(m) =>
				m.IsActive !== false &&
				m.ActivityID === activityID
		)

		if (!matchingMetadata.length) return

		// Bell/push notifications — NOTIFICATION or ACTION alert type
		const bellNotifications = matchingMetadata.filter((m) => {
			const isbell = m.AlertType === 'NOTIFICATION' || m.AlertType === 'ACTION'
			if (!isbell) return false
			return true
		})

		// Deduplicate by ToUserRoles so the same group isn't notified twice
		const deduped = Array.from(
			new Map(bellNotifications.map((m) => [m.ToUserRoles || m.SecurityGroupTitle, m])).values()
		)

		const who = rhybusWhoHttp()

		// For each metadata rule, merge group users + submitter into one array and notify all
		await Promise.all(
			deduped.map(async (meta) => {
				const groupName: string = (meta.ToUserRoles || meta.SecurityGroupTitle || '').trim()
				if (!groupName) return

				let groupUsers: any[] = []
				try {
					groupUsers = [...(await who.Role(groupName))] as any[]
				} catch (err) {
					console.error(`[triggerCaseNotifications] Failed to fetch group "${groupName}":`, err)
					return
				}

				// Collect all recipient emails: group members + submitter (deduplicated)
				const groupEmails: string[] = groupUsers
					.map((u: any) => u?.Email ?? u?.email ?? u?.upn ?? u?.loginName ?? u?.LoginName ?? '')
					.filter(Boolean)
				const allRecipients: string[] = [
					...new Set([...groupEmails, ...(submitterEmail ? [submitterEmail] : [])]),
				]

				await Promise.all(
					allRecipients.map((toUser) =>
						sendNotification({
							title: subject,
							activity: activityID,
							subject: subject,
							notificationType: meta.AlertType === 'ACTION' ? 'Action' : 'Notification',
							status: String(statusCode ?? ''),
							itemGuid: itemGuid,
							toUser,
							toUserRole: meta.ToRole ?? groupName,
						})
					)
				)
			})
		)

		// Refresh Redux store so the bell icon updates for the current user
		const [{ default: store }, { getAllNotifications }] = await Promise.all([
			import('../store'),
			import('./notificationsSlice'),
		])
		store.dispatch(getAllNotifications())
	} catch (err) {
		console.error('[triggerCaseNotifications]', err)
	}
}
